import React from 'react';
import PropTypes from 'prop-types';
import gettext from '@eb/gettext';
import { translationPropType } from '@eb/i18n';
import { isEmpty } from 'lodash';

import ConfirmEmailField from '../ConfirmEmailField';
import FormSubmitButton from '../FormSubmitButton';
import StaticEmailField from '../StaticEmailField';
import SignUpFields from '../SignUpFields';
import LegalNotice from '../LegalNotice';

import { formValidate } from '@eb/eds-utils';
import { Link } from '@eb/eds-link';
import {
    signUpValidate as validate,
    asyncValidateConfirmEmail as asyncValidate,
} from '../utils';

import { LOG_IN_PATH, SIGN_UP_FORM } from '../../constants';

class SignUpForm extends React.PureComponent {
    static propTypes = {
        handleSubmit: PropTypes.func.isRequired,
        buttonCTA: translationPropType,
        email: PropTypes.string,
        handleEmailEdit: PropTypes.func,
        onLoginInsteadClick: PropTypes.func,
        locale: PropTypes.string,
        recaptcha2PublicKey: PropTypes.string,
        showCaptcha: PropTypes.bool,
        fp: PropTypes.bool,
        showNotification: PropTypes.func,
        clearNotification: PropTypes.func,
        socialSSOSettings: PropTypes.shape({
            facebook: PropTypes.object,
        }),
        trackEmailConfirmationEdit: PropTypes.func,
        trackEmailMismatchShowed: PropTypes.func,

        /* Props Provided Via ReduxForm */
        submitFailed: PropTypes.bool,
        submitSucceeded: PropTypes.bool,
        submitting: PropTypes.bool,
    };

    componentWillMount() {
        const {
            shouldIncludeEmailConfirmation,
            trackEmailConfirmationEdit,
        } = this.props;

        if (shouldIncludeEmailConfirmation) {
            trackEmailConfirmationEdit();
        }
    }

    componentWillReceiveProps({ submitting, submitFailed, error }) {
        const { showNotification, clearNotification } = this.props;

        /*If the form has an error and submit failed, attempt
        to display a notification*/
        if (submitFailed && error) {
            showNotification(error);
        }

        /*We want to limit only one error notification to
        appear per form submission if the user has dismissed it.
        So each time submitting is true, then reset the flag.*/
        if (submitting) {
            clearNotification();
        }
    }

    componentWillUnmount() {
        const { clearNotification } = this.props;

        clearNotification();
    }

    _trackLoginInsteadClick = (event) => {
        event.preventDefault();

        const { onLoginInsteadClick } = this.props;

        if (onLoginInsteadClick) {
            onLoginInsteadClick();
        }
    };

    // EXPERIMENT_MARKER=CONFIRM_EMAIL
    render() {
        const {
            formErrors,
            handleSubmit,
            email,
            handleEmailEdit,
            buttonCTA,
            submitting,
            submitSucceeded,
            locale,
            recaptcha2PublicKey,
            showCaptcha,
            fp,
            clearNotification,
            clearAuthNotification,
            submitFailed,
            socialSSOSettings,
            shouldIncludeEmailConfirmation,
            trackEmailMismatchShowed,
        } = this.props;

        const submitDisabled = submitting || submitSucceeded;

        const twoStepsSignup =
            socialSSOSettings && socialSSOSettings.twoStepsSignup;

        const _handleClearNotification = () => {
            clearNotification && clearNotification();
            clearAuthNotification && clearAuthNotification();
        };

        let privacyPolicy = null;

        if (isEmpty(socialSSOSettings) || twoStepsSignup) {
            privacyPolicy = <LegalNotice locale={locale} />;
        }

        let confirmEmailField = null;
        let logInInsteadLink = null;

        if (shouldIncludeEmailConfirmation) {
            confirmEmailField = (
                <ConfirmEmailField
                    email={email}
                    clearNotification={clearNotification}
                    submitFailed={submitFailed}
                />
            );
        }

        if (shouldIncludeEmailConfirmation) {
            logInInsteadLink = (
                <div className="eds-l-mar-top-2 eds-align--center-horizontal">
                    <Link
                        to={LOG_IN_PATH}
                        onClick={this._trackLoginInsteadClick}
                    >
                        {gettext('Log In Instead')}
                    </Link>
                </div>
            );
        }

        return (
            <div className="eds-fx--fade-in eds-fx--delay-2">
                <form onSubmit={handleSubmit} noValidate="novalidate">
                    <StaticEmailField
                        email={email}
                        formErrors={formErrors}
                        onEmailEdit={handleEmailEdit}
                        onEmailMismatch={trackEmailMismatchShowed}
                        clearNotification={_handleClearNotification}
                        shouldRevealEmail={true}
                    />
                    {confirmEmailField}
                    <SignUpFields
                        locale={locale}
                        recaptcha2PublicKey={recaptcha2PublicKey}
                        showCaptcha={showCaptcha}
                        fp={fp}
                        submitFailed={submitFailed}
                        socialSSOSettings={socialSSOSettings}
                        shouldIncludeEmailConfirmation={
                            shouldIncludeEmailConfirmation
                        }
                    />
                    <FormSubmitButton
                        submitDisabled={submitDisabled}
                        buttonClasses="eds-fx--fade-in-down eds-fx--delay-1 eds-l-mar-top-8"
                        buttonCTA={buttonCTA}
                    />
                    {privacyPolicy}
                </form>
                {logInInsteadLink}
            </div>
        );
    }
}

export default formValidate({
    validate,
    asyncValidate,
    form: SIGN_UP_FORM,
    touchOnChange: true,
    touchOnBlur: false,
})(SignUpForm);
